import { useEffect, useState } from 'react';

import { useEngineBus } from '../hooks/useEngineBus.js';

/**
 * How long a finished sprint keeps the bar on screen before it fades back out,
 * in ms. It is a HOLD, not a delay: the eye arrives at the bottom-left cluster
 * AFTER the thumb comes off Shift, so a bar that vanished on the last publish
 * would be gone by the time anybody looked at it — the player would see a
 * flicker in their peripheral vision and never learn what it was. Long enough
 * to read a 220 px bar, short enough that it is not chrome.
 */
const HOLD_MS = 900;

/** Fade duration, ms. Paired with the Tailwind class below — keep them equal. */
const FADE_MS = 300;

/**
 * @file The stamina readout — the thin strip at the bottom of the bottom-left
 * cluster, under `HealthBar` and above the minimap. It reports one number the
 * player spends by holding Shift, and it is deliberately the QUIETEST slab in
 * the column: no display numeral, no segments, no accent colour while things
 * are going well. Health and ammo are readings you make DECISIONS on mid-fight;
 * stamina is a reading you GLANCE at, and a third loud slab in the corner of
 * the eye would cost the two loud ones their loudness.
 *
 * It is 4 px of fill inside a 1 px keyline — half the height of `HealthBar`'s
 * segments and a third of the whole health slab — for the same reason.
 *
 * ⚠ This component does NOT position itself, for the same reason `AmmoBar`,
 * `HealthBar` and `CashBar` do not: the bottom-left cluster in
 * `routes/PlayRoute.jsx` owns the layout, and phone/ammo/health/stamina/minimap
 * share ONE column and ONE width. Five self-positioning slabs drift apart the
 * moment any of them changes size.
 *
 * It returns `null` until the first `'player:stamina'`, so a level with no
 * stamina model (every dev scene, and the whole game before this feature)
 * renders exactly as it did before this file existed — the `CashBar` idiom. The
 * model publishes ONCE ON CONSTRUCTION (the `Wallet.js` rule), which is what
 * lets the slot exist from the first frame instead of appearing the first time
 * somebody runs.
 *
 * ⚠ It is mounted INSIDE the gameplay-chrome group, so it stands down for a
 * phone call with the rest of the column — by OPACITY, never by unmounting,
 * because it holds a live bus subscription and returns `null` until that
 * subscription has spoken. A remount would drop the very snapshot that makes it
 * correct when it comes back, and on a frozen loop "the next publish" can be a
 * long time (`PhoneCall.jsx`, `docs/ui.md`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT DECIDES NOTHING AND KEEPS NO COPY
 * ─────────────────────────────────────────────────────────────────────────────
 * The channel carries the whole state every time (`{value, sprinting,
 * exhausted}`), so there is no stamina level held here, no drain rate here, no
 * recovery clock here and — importantly — **no second opinion on whether the
 * player may run**. `exhausted` is the model's LATCH, arriving as a boolean;
 * this file renders it and never derives it. A `canSprint` computed here would
 * be the second gate that the `hurtPlayer(amount, lethal)` family of bug is made
 * of: two readings of "may this happen", disagreeing in the one state nobody
 * tests.
 *
 * ⚠ **THERE IS NO 35% RECOVERY TICK ON THE TRACK**, tempting as one is. That
 * threshold is the model's own constant, and a mark drawn here would be a second
 * copy of it that nothing keeps in step — the `links.js`/`countryroads.js` trap,
 * in a file that cannot even see the number. The exhausted state is communicated
 * by SAYING SO, which is true whatever the threshold is.
 *
 * The one thing this file does hold is `shown`, and that is presentation, not
 * model state: it is "has the bar been asked for recently", derived from the
 * payload on arrival and reset by a timer. It is not a mirror of `value`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ EXHAUSTED IS LEGIBLE WITHOUT MOTION
 * ─────────────────────────────────────────────────────────────────────────────
 * `AmmoBar`'s SAFE rule, verbatim: a state communicated only by a blink or a
 * pulse reads as "my game is glitching", and the report that follows is *"the
 * run button stopped working"* thirty seconds later — the same class of report
 * the 9 m `FIRE.range` produced. So exhaustion is THREE static changes at once:
 * the fill goes siren, the empty part of the track takes a static diagonal
 * hatch, and the word `EXHAUSTED` sits in the label row. Colour alone would also
 * fail a red-green colour-blind player; the hatch and the word do not.
 *
 * There is a second, independent reason no CSS animation may appear in this
 * file: `docs/weapons.md` records it — **an animation keeps running while an
 * overlay has the sim frozen**, so a pulsing bar under a pause menu would be
 * reporting motion in a world that has stopped.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ NO TAP ZONE — AND THAT IS A DECISION, NOT AN OMISSION
 * ─────────────────────────────────────────────────────────────────────────────
 * `AmmoBar` has two tap zones because this game targets MOBILE and reload and
 * safe are DISCRETE ACTIONS with no other touch path — on a phone there is no
 * `R` and no `X`, so without the buttons those two verbs do not exist at all.
 *
 * A sprint is not a discrete action. It is a HELD state, it belongs to MOVEMENT,
 * and this game's touch story for movement is the drag-yaw / stick path, not the
 * HUD. A "sprint" button here would be a second, differently-behaved way to ask
 * for the same thing — one that latches where the real input holds, that lives
 * 220 px from the thumb already steering, and that would end up with rules of
 * its own (the `shops.press()` vs `'shop:buy'` shape this project warns about).
 *
 * ⚠ **A touch sprint affordance belongs with the MOVEMENT INPUT**, beside the
 * stick that already owns direction — not on the readout that reports its cost.
 * When it is built it should end in the same held-input path the keyboard uses,
 * exactly as `AmmoBar`'s two zones end in `Weapons.js`'s own methods.
 *
 * So nothing here opts back into `pointer-events`, and a drag across this strip
 * still reaches the canvas. `role="progressbar"` + an `aria-label` carry the
 * state to assistive tech instead. ⚠ It is NOT a `role="status"` live region
 * (the `AmmoBar` rule): `value` is quantised to 1/100 and published on change,
 * so a sprint emits it many times a second and a live region would read the
 * whole slab out on every one of them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE HIDDEN SLOT IS RESERVED, NOT COLLAPSED
 * ─────────────────────────────────────────────────────────────────────────────
 * A permanently-full stamina bar is chrome noise, so the widget fades out while
 * `value >= 1 && !sprinting` — but it stays MOUNTED and it keeps its box. If it
 * collapsed instead, the health slab and the minimap would slide down every time
 * the player started running and slide back up every time they stopped: a HUD
 * that reflows under the reading you are taking is worse than 20 px of
 * transparent space that never moves. The height is constant across all three
 * states for the same reason — the label row is always present, so the word
 * `EXHAUSTED` arriving cannot push the column about.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 * @param {number} [props.width=220] CSS px — match the minimap.
 */
export default function StaminaBar({ engine, width = 220 }) {
  // 100 ms matches `AmmoBar` and `HealthBar`. The model publishes ON CHANGE
  // ONLY and has already quantised `value` to 1/100, so this throttle is
  // coalescing a change stream that is bounded by construction, not taming a
  // per-frame firehose. It never lags a drain the eye can follow.
  const stam = useEngineBus(engine, 'player:stamina', { throttle: 100 });

  // Every read is guarded the way `Weapons.js` guards its own def fields: a
  // malformed payload must render a wrong-but-whole HUD, never a NaN.
  const value = Math.max(0, Math.min(1, Number(stam?.value) || 0));
  const sprinting = !!stam?.sprinting;
  const exhausted = !!stam?.exhausted;

  // "Nothing to report": full, not running, not latched. ⚠ NO PAYLOAD COUNTS AS
  // IDLE, and the order of that test matters — with `!stam` last, a null payload
  // would read as `value 0` (i.e. empty, i.e. interesting) and the bar would
  // flash on for one HOLD_MS at boot before anything had happened.
  const idle = !stam || (value >= 1 && !sprinting && !exhausted);

  const [shown, setShown] = useState(false);
  useEffect(() => {
    // Reveal is IMMEDIATE and hide is on a timer, never the reverse: the frame
    // the player starts running is the frame the bar has to be there.
    if (!idle) {
      setShown(true);
      return undefined;
    }
    const t = setTimeout(() => setShown(false), HOLD_MS);
    return () => clearTimeout(t);
  }, [idle]);

  if (!stam) return null;

  // Quiet by default: slate, the same neutral the header text uses, and NOT one
  // of the palette's three accents. Sprinting only BRIGHTENS it — a third accent
  // colour in this column would compete with health's green/amber/red and ammo's
  // white/amber/red for a reading nobody acts on. Siren is reserved for the one
  // state that stops the player doing something.
  const tone = exhausted ? '#ff2e4d' : sprinting ? '#d7dcea' : '#8b93a7';

  return (
    <div
      className="select-none transition-opacity"
      style={{
        width: `${width}px`,
        opacity: shown ? 1 : 0,
        transitionDuration: `${FADE_MS}ms`,
      }}
      // Faded out is not "quieter" to a screen reader, it is invisible — so the
      // widget leaves the accessibility tree with the pixels, the same way
      // `PlayRoute` takes the whole chrome group out of it during a call.
      aria-hidden={!shown}
    >
      {/* The label row is ALWAYS present (see the header): it is what keeps the
          slab one height in all three states. `EXHAUSTED` replaces nothing and
          pushes nothing — it fills a slot that is already there. */}
      <div className="flex items-baseline justify-between px-0.5 pb-[3px] font-hud text-[9px] font-bold uppercase leading-none tracking-[0.22em]">
        <span className="text-hud-faint">Stamina</span>
        {exhausted ? <span className="text-siren">Exhausted</span> : null}
      </div>

      <div
        className="overflow-hidden rounded-[2px] border border-hairline bg-void/80"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
        aria-label={
          exhausted
            ? `Stamina ${Math.round(value * 100)} percent — exhausted, cannot run`
            : `Stamina ${Math.round(value * 100)} percent${sprinting ? ' — running' : ''}`
        }
        style={{
          // TREATMENT ONE OF TWO for exhausted, and it is STATIC: a hard 45°
          // hatch across the whole track, visible in the empty part the fill has
          // vacated. It survives greyscale and it survives being looked at for
          // thirty seconds, which a blink does not.
          backgroundImage: exhausted
            ? 'repeating-linear-gradient(45deg, rgba(255,46,77,0.30) 0 3px, rgba(255,46,77,0) 3px 6px)'
            : 'none',
        }}
      >
        <div
          className="h-[4px] rounded-[1px]"
          style={{
            // Percent, not segments: `HealthBar`'s armour-strip argument. This
            // is a continuous quantity draining smoothly, and chunking it would
            // make a sprint look free for its first eighth.
            width: `${value * 100}%`,
            backgroundColor: tone,
            // The same hard inner highlight the health segments and the ammo
            // pips carry, so 4 px of bar still reads as a lit thing rather than
            // as a coloured line.
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.24)',
          }}
        />
      </div>
    </div>
  );
}
